'use client';

import { useTheme } from '@/contexts/ThemeContext';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';

export interface LogoProps {
  /** Width of the logo in pixels */
  width?: number;
  /** Height of the logo in pixels */
  height?: number;
  /** Show brand name text next to the logo */
  showText?: boolean;
  /** Show tagline under brand name */
  showTagline?: boolean;
  /** Custom className for the container */
  className?: string;
  /** Text size variant */
  textSize?: 'sm' | 'md' | 'lg' | 'xl';
  /** Force dark logo regardless of theme (for dark backgrounds) */
  forceDark?: boolean;
  /** Use logo with name embedded in SVG (for login/register pages) */
  withName?: boolean;
  /** Scale logo to fit container width (for responsive header) */
  responsive?: boolean;
}

/**
 * Logo component that displays the brand logo from /public/brand folder
 * Automatically switches between light and dark logo based on theme
 * Uses SVG logos: without name for header/footer/sidebar, with name for auth pages
 */
export default function Logo({
  width = 48,
  height = 48,
  showText = true,
  showTagline = false,
  className = '',
  textSize = 'lg',
  forceDark = false,
  withName = false,
  responsive = false,
}: LogoProps) {
  const { theme } = useTheme();
  const t = useTranslations('nav');
  const locale = useLocale();
  const isDark = theme === 'dark';

  // Select logo based on theme, forceDark prop, and withName prop
  // If withName is true, use logos with name embedded (for login/register)
  // Otherwise, use logos without name (for header/footer/sidebar)
  const logoSrc = withName
    ? forceDark || isDark
      ? '/brand/darklogoandname.svg'
      : '/brand/lightlogoandname.svg'
    : forceDark || isDark
      ? '/brand/darklogo.svg'
      : '/brand/lightlogo.svg';

  // Text size classes
  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-xl',
    xl: 'text-2xl',
  };

  const taglineSizeClasses = {
    sm: 'text-xs',
    md: 'text-xs sm:text-sm',
    lg: 'text-xs sm:text-sm',
    xl: 'text-sm',
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Image
        src={logoSrc}
        alt={t('brandName')}
        width={width}
        height={height}
        className={responsive ? 'h-auto w-full max-w-full object-contain' : 'object-contain'}
        style={responsive ? { height: 'auto', maxWidth: '100%' } : undefined}
        priority
      />
      {showText && (
        <div className="flex flex-col">
          <span className={`font-bold ${textSizeClasses[textSize]} text-white`}>
            {t('brandName')}
          </span>
          {showTagline && (
            <span className={`${taglineSizeClasses[textSize]} text-white`}>
              {t('brandTagline')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Logo icon only - for compact spaces (header, footer, sidebar)
 * Always uses logo without name
 */
export function LogoIcon({
  width = 40,
  height = 40,
  className = '',
  forceDark = false,
}: {
  width?: number;
  height?: number;
  className?: string;
  forceDark?: boolean;
}) {
  const { theme } = useTheme();
  const t = useTranslations('nav');
  const isDark = theme === 'dark';

  // LogoIcon always uses logo without name (for header/footer/sidebar)
  const logoSrc = forceDark || isDark ? '/brand/darklogo.svg' : '/brand/lightlogo.svg';

  return (
    <Image
      src={logoSrc}
      alt={t('brandName')}
      width={width}
      height={height}
      className={`object-contain ${className}`}
      priority
    />
  );
}
